/**
 * mfaHandoff — how a pending MFA step reaches the /auth/mfa pages.
 *
 * Password login and the SSO callback both store the challenge / enrol token
 * (plus the deep link) through `setPending` and navigate in-app. Tokens never
 * travel in the URL: the SSO callback exchanges a one-time code for them. The
 * MFA pages call `takeToken`, which also tolerates a legacy ?token=&next=
 * hand-off by moving it into sessionStorage so nothing lingers in history.
 */
const KEYS = { challenge: 'mfaChallengeToken', enroll: 'mfaEnrollToken' } as const;
const NEXT_KEY = 'mfaNext';

export type PendingKind = keyof typeof KEYS;

const inApp = (p: string | null): p is string =>
  !!p && /^\/(?![/\\])/.test(p) && !p.includes('\\');

/** Return the pending token and deep link for `kind`, stashing a URL-carried
 *  token in sessionStorage. Call once on page entry. */
export function takeToken(kind: PendingKind): { token: string; next: string } {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');
  const urlNext = params.get('next');
  if (urlToken) {
    // A fresh hand-off replaces both the token and the deep link, so an
    // abandoned earlier attempt's `next` never leaks into this one.
    sessionStorage.setItem(KEYS[kind], urlToken);
    if (inApp(urlNext)) sessionStorage.setItem(NEXT_KEY, urlNext);
    else sessionStorage.removeItem(NEXT_KEY);
  } else if (urlNext !== null) {
    if (inApp(urlNext)) sessionStorage.setItem(NEXT_KEY, urlNext);
    else sessionStorage.removeItem(NEXT_KEY);
  }
  const next = sessionStorage.getItem(NEXT_KEY) || '';
  return { token: sessionStorage.getItem(KEYS[kind]) || '', next: inApp(next) ? next : '' };
}

/** Store a pending MFA step (token + where to go afterwards), replacing any
 *  earlier abandoned one so its deep link cannot leak into this attempt. */
export function setPending(kind: PendingKind, token: string, next?: string): void {
  clearPending(kind);
  sessionStorage.setItem(KEYS[kind], token);
  if (inApp(next ?? null)) sessionStorage.setItem(NEXT_KEY, next as string);
}

/** True when the current URL carries the token (legacy hand-off); the page should
 *  replace the URL so the token is not kept in history. */
export function urlCarriesToken(): boolean {
  return new URLSearchParams(window.location.search).has('token');
}

export function clearPending(kind: PendingKind): void {
  sessionStorage.removeItem(KEYS[kind]);
  sessionStorage.removeItem(NEXT_KEY);
}
